import React, { useEffect, useMemo, useRef, useState } from 'react';
import contributors from '../data/contributors.json';
import bilateralPairs from '../data/bilateral-pairs.json';
import capitalCoords from '../data/capital_coords.json';
import landRings from '../data/land-110m.json';
import {
  lonLatToXYZ,
  rotateY,
  project,
  arcPath,
  goldenSpiralPoints,
  type Vec3
} from '../lib/globeMath';
import { useGlobeRotation } from '../hooks/useGlobeRotation';
import { useGlobeInteraction } from '../hooks/useGlobeInteraction';
import { useGeoDetection } from '../hooks/useGeoDetection';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { GlobeTooltip } from './GlobeTooltip';

type Dot = { cc: string; lat: number; lon: number; tier: string };
type Contrib = { cc1: string; cc2: string | null; tier: string };
type CapitalMap = Record<string, { lat: number; lon: number; name?: string | null }>;

const TIER_BASE_COLOR: Record<string, [number, number, number]> = {
  contributor: [184, 155, 100],
  expert: [184, 155, 100],
  chief: [184, 155, 100],
  kerim: [245, 232, 180]
};

const TIER_MAX_ALPHA: Record<string, number> = {
  contributor: 0.55,
  expert: 0.7,
  chief: 0.9,
  kerim: 1.0
};

const TIER_RADIUS: Record<string, number> = {
  contributor: 1.6,
  expert: 1.8,
  chief: 2.2,
  kerim: 3.5
};

export function DotGlobe({ size: maxSize = 520 }: { size?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const reduced = useReducedMotion();

  const [size, setSize] = useState(() => {
    if (typeof window === 'undefined') return maxSize;
    return Math.min(maxSize, Math.max(240, window.innerWidth - 32));
  });
  useEffect(() => {
    const update = () => {
      const parent = containerRef.current?.parentElement;
      const available = parent ? parent.clientWidth : window.innerWidth - 32;
      setSize(Math.min(maxSize, Math.max(240, available)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [maxSize]);

  const { theta, setTheta } = useGlobeRotation({ paused: !!tooltip, containerRef });
  const { dragging } = useGlobeInteraction(canvasRef, setTheta);
  const geo = useGeoDetection();

  // Center globe on the visitor's longitude once, when geo arrives.
  // theta = -lon converts visitor longitude into the Z-axis viewing centre.
  const didCenterRef = useRef(false);
  useEffect(() => {
    if (didCenterRef.current) return;
    if (geo.lon == null) return;
    didCenterRef.current = true;
    setTheta(-geo.lon * Math.PI / 180);
  }, [geo.lon, setTheta]);

  const dotsXYZ = useMemo<Array<{ cc: string; tier: string; p: Vec3 }>>(
    () =>
      (contributors as Dot[]).map(d => ({
        cc: d.cc,
        tier: d.tier,
        p: lonLatToXYZ(d.lon, d.lat)
      })),
    []
  );
  const graticule = useMemo<Vec3[]>(() => goldenSpiralPoints(900), []);

  const landXYZ = useMemo<Vec3[][]>(
    () =>
      (landRings as number[][][]).map((ring) =>
        ring.map(([lon, lat]) => lonLatToXYZ(lon, lat))
      ),
    []
  );

  const ccCount = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const d of contributors as Dot[]) m[d.cc] = (m[d.cc] || 0) + 1;
    return m;
  }, []);

  const contributorCCs = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const d of contributors as Dot[]) set.add(d.cc.toUpperCase());
    return Array.from(set);
  }, []);

  // Real bilateral constellation: each contributor with cc1+cc2 draws
  // an arc between the two country capitals. No fake NYC hub.
  const constellation = useMemo<
    Array<{ cc1: string; cc2: string; a: Vec3; b: Vec3; isChief: boolean }>
  >(() => {
    const caps = capitalCoords as CapitalMap;
    const seen = new Set<string>();
    const arcs: Array<{ cc1: string; cc2: string; a: Vec3; b: Vec3; isChief: boolean }> = [];
    for (const c of bilateralPairs as Contrib[]) {
      if (!c.cc1 || !c.cc2) continue;
      const k1 = c.cc1.toUpperCase();
      const k2 = c.cc2.toUpperCase();
      // Deduplicate: one arc per unique country pair
      const key = k1 < k2 ? `${k1}_${k2}` : `${k2}_${k1}`;
      if (seen.has(key)) continue;
      const cap1 = caps[k1];
      const cap2 = caps[k2];
      if (!cap1 || !cap2) continue;
      seen.add(key);
      arcs.push({
        cc1: k1,
        cc2: k2,
        a: lonLatToXYZ(cap1.lon, cap1.lat),
        b: lonLatToXYZ(cap2.lon, cap2.lat),
        isChief: c.tier === 'chief'
      });
    }
    return arcs;
  }, []);

  // Initialise canvas dimensions/dpr scaling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
  }, [size]);

  // Draw on every theta change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;

    ctx.clearRect(0, 0, size, size);

    // Outer atmospheric glow
    const glow = ctx.createRadialGradient(cx, cy, radius * 0.95, cx, cy, radius * 1.18);
    glow.addColorStop(0, 'rgba(184, 155, 100, 0.09)');
    glow.addColorStop(1, 'rgba(184, 155, 100, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 1.18, 0, Math.PI * 2);
    ctx.fill();

    // Globe disc with subtle shading
    const disc = ctx.createRadialGradient(
      cx - radius * 0.35,
      cy - radius * 0.35,
      radius * 0.2,
      cx,
      cy,
      radius
    );
    disc.addColorStop(0, 'rgba(32, 44, 72, 0.85)');
    disc.addColorStop(1, 'rgba(12, 18, 32, 0.9)');
    ctx.fillStyle = disc;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    // Continent coastlines — legible but still quiet. Stroke + very faint
    // warm-tinted fill so landmasses read as "where contributors are from"
    // without competing with the gold contributor dots.
    ctx.lineWidth = 0.9;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const ring of landXYZ) {
      if (ring.length < 2) continue;
      ctx.beginPath();
      let drawing = false;
      let prevDepth = 0;
      let sumDepth = 0;
      let segCount = 0;
      for (let i = 0; i < ring.length; i++) {
        const r = rotateY(ring[i], theta);
        const px = cx + r.x * radius;
        const py = cy - r.y * radius;
        const visible = r.z >= 0;
        if (visible) {
          if (drawing) {
            ctx.lineTo(px, py);
            sumDepth += (prevDepth + r.z) * 0.5;
            segCount++;
          } else {
            ctx.moveTo(px, py);
            drawing = true;
          }
          prevDepth = r.z;
        } else {
          drawing = false;
          prevDepth = 0;
        }
      }
      if (segCount === 0) continue;
      const avgDepth = sumDepth / segCount;
      const fillAlpha = 0.04 + avgDepth * 0.06;
      ctx.fillStyle = `rgba(184, 155, 100, ${fillAlpha.toFixed(3)})`;
      ctx.fill();
      const strokeAlpha = 0.18 + avgDepth * 0.28;
      ctx.strokeStyle = `rgba(170, 188, 214, ${strokeAlpha.toFixed(3)})`;
      ctx.stroke();
    }

    // Graticule — z-depth fade
    for (const g of graticule) {
      const r = rotateY(g, theta);
      const proj = project(r, radius, cx, cy);
      if (!proj.visible) continue;
      const a = 0.05 + proj.depth * 0.15;
      ctx.fillStyle = `rgba(184, 155, 100, ${a.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, 0.7, 0, Math.PI * 2);
      ctx.fill();
    }

    // Contributor dots — z-depth modulated opacity + size
    for (const d of dotsXYZ) {
      const r = rotateY(d.p, theta);
      const proj = project(r, radius, cx, cy);
      if (!proj.visible) continue;
      const [cr, cg, cb] = TIER_BASE_COLOR[d.tier] || TIER_BASE_COLOR.contributor;
      const maxA = TIER_MAX_ALPHA[d.tier] ?? 0.55;
      const a = maxA * (0.25 + 0.75 * proj.depth);
      const rad = (TIER_RADIUS[d.tier] || 1.6) * (0.7 + 0.3 * proj.depth);
      ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${a.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, rad, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [theta, size, dotsXYZ, graticule, landXYZ]);

  // Constellation paths — real bilateral arcs, recomputed on rotation
  const constellationPaths = useMemo(() => {
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;
    const visitorCC = (geo.cc || '').toUpperCase();
    const paths: Array<{
      key: string;
      d: string;
      alpha: number;
      isVisitor: boolean;
      isChief: boolean;
    }> = [];
    for (const arc of constellation) {
      const ra = rotateY(arc.a, theta);
      const rb = rotateY(arc.b, theta);
      if (ra.z < 0 && rb.z < 0) continue;
      const avgZ = (ra.z + rb.z) / 2;
      const alpha = 0.15 + Math.max(0, avgZ) * 0.28;
      const isVisitor =
        visitorCC !== '' &&
        (arc.cc1 === visitorCC || arc.cc2 === visitorCC);
      paths.push({
        key: `${arc.cc1}_${arc.cc2}`,
        d: arcPath(ra, rb, radius, cx, cy),
        alpha,
        isVisitor,
        isChief: arc.isChief
      });
    }
    return paths;
  }, [constellation, theta, size, geo.cc]);

  // Continent name labels — one per continent, positioned at a rough
  // centroid. Hidden when the centroid is on the back of the globe; faded
  // near the limb so the labels read as discreet orientation, not chrome.
  const continentLabels = useMemo(() => {
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;
    const CONTINENTS: Array<{ name: string; lon: number; lat: number }> = [
      { name: 'NORTH AMERICA', lon: -100, lat: 45 },
      { name: 'SOUTH AMERICA', lon: -60, lat: -15 },
      { name: 'EUROPE',        lon:  15, lat: 52 },
      { name: 'AFRICA',        lon:  20, lat:  2 },
      { name: 'ASIA',          lon:  90, lat: 42 },
      { name: 'OCEANIA',       lon: 135, lat: -25 },
    ];
    const out: Array<{ name: string; x: number; y: number; alpha: number }> = [];
    for (const c of CONTINENTS) {
      const r = rotateY(lonLatToXYZ(c.lon, c.lat), theta);
      if (r.z < 0.15) continue; // hide near terminator / back side
      const proj = project(r, radius, cx, cy);
      // Alpha: zero at edges, peaks mid-face
      const a = Math.max(0, (r.z - 0.15) / 0.6) * 0.55;
      if (a <= 0) continue;
      out.push({ name: c.name, x: proj.x, y: proj.y, alpha: Math.min(0.55, a) });
    }
    return out;
  }, [theta, size]);

  // Visitor location label — pick the capital of the nearest country that
  // has contributors (so someone in Hua Hin sees BANGKOK, not Hua Hin).
  // Uses visitor lat/lon if geo-detected, else the geo.cc capital as origin.
  const visitorLabel = useMemo(() => {
    if (!geo.cc && geo.lat == null) return null;
    const caps = capitalCoords as CapitalMap;
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;
    const originLat = geo.lat != null ? geo.lat : caps[(geo.cc || '').toUpperCase()]?.lat;
    const originLon = geo.lon != null ? geo.lon : caps[(geo.cc || '').toUpperCase()]?.lon;
    if (originLat == null || originLon == null) return null;

    // Great-circle distance (haversine) to each contributor-country capital.
    const toRad = (d: number) => (d * Math.PI) / 180;
    const oLat = toRad(originLat);
    const oLon = toRad(originLon);
    let best: { cc: string; lat: number; lon: number; name: string; dist: number } | null = null;
    for (const cc of contributorCCs) {
      const cap = caps[cc];
      if (!cap || !cap.name) continue;
      const pLat = toRad(cap.lat);
      const pLon = toRad(cap.lon);
      const dLat = pLat - oLat;
      const dLon = pLon - oLon;
      const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(oLat) * Math.cos(pLat) * Math.sin(dLon / 2) ** 2;
      const dist = 2 * Math.asin(Math.sqrt(h));
      if (!best || dist < best.dist) {
        best = { cc, lat: cap.lat, lon: cap.lon, name: cap.name, dist };
      }
    }
    if (!best) return null;

    const r = rotateY(lonLatToXYZ(best.lon, best.lat), theta);
    if (r.z < 0.2) return null;
    const proj = project(r, radius, cx, cy);
    return {
      text: best.name.toUpperCase(),
      x: proj.x,
      y: proj.y,
      alpha: Math.min(0.85, Math.max(0, (r.z - 0.2) / 0.6) * 0.9),
    };
  }, [geo, theta, size, contributorCCs]);

  // Visitor-specific arc from geo-detected coords to nearest matching capital
  const visitorArc = useMemo(() => {
    if (!geo.cc || geo.lat == null || geo.lon == null) return null;
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;
    const caps = capitalCoords as CapitalMap;
    const visitorCC = geo.cc.toUpperCase();
    const cap = caps[visitorCC];
    if (!cap) return null;
    const a = rotateY(lonLatToXYZ(geo.lon, geo.lat), theta);
    const b = rotateY(lonLatToXYZ(cap.lon, cap.lat), theta);
    // Only show if origin and capital differ enough (visitor not in capital city)
    const distPx = Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
    if (distPx < 0.02) return null; // same point
    if (a.z < 0 && b.z < 0) return null;
    return {
      d: arcPath(a, b, radius, cx, cy, 1.1),
      origin: project(a, radius, cx, cy),
      visible: a.z >= 0
    };
  }, [geo, theta, size]);

  const handleTap = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;
    let best: { dist: number; cc: string; x: number; y: number } | null = null;
    for (const d of dotsXYZ) {
      const r = rotateY(d.p, theta);
      const proj = project(r, radius, cx, cy);
      if (!proj.visible) continue;
      const dx = proj.x - px,
        dy = proj.y - py;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 16 && (!best || dist < best.dist)) {
        best = { dist, cc: d.cc, x: proj.x, y: proj.y };
      }
    }
    if (best) {
      setTooltip({
        x: best.x,
        y: best.y,
        text: `${best.cc.toUpperCase()} · ${ccCount[best.cc] || 1} contributors`
      });
    } else {
      setTooltip(null);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTooltip(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={containerRef}
      onPointerUp={handleTap}
      style={{
        position: 'relative',
        width: size,
        height: size,
        touchAction: 'pan-y',
        cursor: dragging ? 'grabbing' : 'grab'
      }}
      role="img"
      aria-label="Interactive globe showing bilateral connections across 193 countries"
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', cursor: dragging ? 'grabbing' : 'grab' }}
      />
      {constellationPaths.length > 0 && (
        <svg
          width={size}
          height={size}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        >
          {/* Background constellation — all bilateral arcs */}
          <g fill="none" strokeLinecap="round">
            {constellationPaths
              .filter(p => !p.isVisitor)
              .map(p => (
                <path
                  key={p.key}
                  d={p.d}
                  stroke="var(--color-accent)"
                  strokeWidth={0.75}
                  strokeOpacity={p.alpha.toFixed(3)}
                />
              ))}
          </g>

          {/* Visitor-country arcs — highlighted over the constellation */}
          <g fill="none" strokeLinecap="round">
            {constellationPaths
              .filter(p => p.isVisitor)
              .map(p => (
                <path
                  key={p.key}
                  d={p.d}
                  stroke="var(--color-highlight, #f5e8b4)"
                  strokeWidth={1.4}
                  strokeOpacity={Math.min(1, p.alpha * 3).toFixed(3)}
                  style={{
                    filter: 'drop-shadow(0 0 2px rgba(245, 232, 180, 0.4))'
                  }}
                />
              ))}
          </g>

          {/* Continent labels — discreet orientation text */}
          <g pointerEvents="none">
            {continentLabels.map((l) => (
              <text
                key={l.name}
                x={l.x}
                y={l.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--color-body-dim, #d0c9b8)"
                fillOpacity={l.alpha.toFixed(3)}
                style={{
                  fontSize: size < 360 ? 8 : 9,
                  letterSpacing: '0.22em',
                  fontWeight: 400,
                  fontFamily: 'var(--font-body), system-ui, sans-serif',
                  userSelect: 'none',
                  textShadow: '0 0 6px rgba(8, 12, 24, 0.8)',
                }}
              >
                {l.name}
              </text>
            ))}
          </g>

          {/* Visitor capital label */}
          {visitorLabel && (
            <g pointerEvents="none">
              <text
                x={visitorLabel.x}
                y={visitorLabel.y + 14}
                textAnchor="middle"
                fill="var(--color-highlight, #f5e8b4)"
                fillOpacity={visitorLabel.alpha.toFixed(3)}
                style={{
                  fontSize: 9,
                  letterSpacing: '0.18em',
                  fontWeight: 500,
                  fontFamily: 'var(--font-body), system-ui, sans-serif',
                  userSelect: 'none',
                  textShadow: '0 0 6px rgba(8, 12, 24, 0.9)',
                }}
              >
                {visitorLabel.text}
              </text>
            </g>
          )}

          {/* Visitor's personal arc — geo position to their capital */}
          {visitorArc && (
            <g>
              <path
                d={visitorArc.d}
                stroke="var(--color-highlight, #f5e8b4)"
                strokeWidth={1.8}
                fill="none"
                strokeLinecap="round"
                style={{
                  filter: 'drop-shadow(0 0 3px rgba(245, 232, 180, 0.55))',
                  strokeDasharray: reduced ? undefined : 600,
                  strokeDashoffset: reduced ? undefined : 600,
                  animation: reduced ? 'none' : 'arc-draw 1.4s ease-out forwards'
                }}
              />
              {visitorArc.visible && (
                <circle
                  cx={visitorArc.origin.x}
                  cy={visitorArc.origin.y}
                  r={3}
                  fill="var(--color-highlight, #f5e8b4)"
                  style={{
                    filter: 'drop-shadow(0 0 4px rgba(245, 232, 180, 0.75))'
                  }}
                />
              )}
            </g>
          )}
        </svg>
      )}
      {tooltip && <GlobeTooltip visible x={tooltip.x} y={tooltip.y} text={tooltip.text} />}
      <style>{`
        @keyframes arc-draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}
